/**
 * Development only: turn flags on for one local dev server without saving them
 * in a shared environment.
 *
 *   NEXT_PUBLIC_LOCAL_FLAG_OVERRIDES=survey-builder-native,feasibility-dani
 *
 * A listed key reads as on wherever flags are read (the server prefetch in
 * server.ts and the browser check in FeatureFlagService.checkAccess); any other
 * key keeps the API's answer. A production build ignores the variable, and
 * every deployed build is a production build, so it cannot reach a client.
 * Server-side gates that read flags themselves (for example Builder's
 * `_lib/access.ts`, which lets internal staff in regardless) are unchanged.
 */
import type { FeatureFlagMap } from '@/@feature-flags/types/primitives';

import { publicEnv } from '@/lib/config/public-env';

export function localFlagOverrides(
    raw: string | undefined = publicEnv('NEXT_PUBLIC_LOCAL_FLAG_OVERRIDES'),
    nodeEnv: string | undefined = process.env.NODE_ENV,
): string[] {
    if (nodeEnv === 'production' || !raw) return [];
    return raw
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);
}

/** The API's flags, with each requested key that is overridden set on. */
export function withLocalFlagOverrides(
    flags: FeatureFlagMap,
    keys: readonly string[],
    overrides: readonly string[] = localFlagOverrides(),
): FeatureFlagMap {
    if (overrides.length === 0) return flags;
    const out = { ...flags };
    for (const key of keys) if (overrides.includes(key)) out[key] = true;
    return out;
}
